import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Card, CardContent, Button,
  Select, MenuItem, Skeleton, Popover,
  Checkbox, ListItemText, OutlinedInput, ListSubheader, TextField, InputAdornment, IconButton
} from '@mui/material';
import { GlobalDataTable } from './DataTable/GlobalDataTable';
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import {
  MRT_ShowHideColumnsButton as MrtShowHideColumnsButton,
  MRT_ToggleDensePaddingButton as MrtToggleDensePaddingButton,
  MRT_ToggleFiltersButton as MrtToggleFiltersButton,
  MRT_ToggleFullScreenButton as MrtToggleFullScreenButton,
} from "material-react-table";
import SearchOffRoundedIcon from "@mui/icons-material/SearchOffRounded";
import SearchIcon from "@mui/icons-material/Search";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import axios from 'axios';
import dayjs from 'dayjs';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import InsertChartOutlinedIcon from '@mui/icons-material/InsertChartOutlined';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import TodayRoundedIcon from '@mui/icons-material/TodayRounded';
import DateRangeRoundedIcon from '@mui/icons-material/DateRangeRounded';

import SingleDatePicker from './SingleDatePicker';

const DATE_FILTERS = ['Today', 'Custom Date'];

/* ── Skeleton helpers ── */
const TableSkeletonRows = ({ rows = 8, cols = 10 }) => (
  <>
    {Array.from({ length: rows }).map((_, i) => (
      <tr key={i} style={{ borderBottom: '1px solid #F1F5F9' }}>
        {Array.from({ length: cols }).map((_, j) => (
          <td key={j} style={{ padding: '14px 16px' }}>
            <Skeleton variant="text" height={16} sx={{ borderRadius: 1, bgcolor: '#F1F5F9' }} />
          </td>
        ))}
      </tr>
    ))}
  </>
);

/* ── Helpers ── */
const getDateRange = (filter, customRange) => {
  const today = dayjs().format('YYYY-MM-DD');
  if (filter === 'Today') return { start_date: today, end_date: today };
  // if (filter === '7 Days') return { start_date: dayjs().subtract(6, 'day').format('YYYY-MM-DD'), end_date: today };
  if (filter === 'Custom Date' && customRange.startDate && customRange.endDate) {
    return { start_date: customRange.startDate, end_date: customRange.endDate };
  }
  return {};
};

const getPageIcon = (pageName, isSelected) => {
  const color = isSelected ? '#FFFFFF' : '#94A3B8';
  const sx = { fontSize: '1.05rem', mr: 0.7, color, transition: 'color 0.2s' };
  switch (pageName) {
    case 'HCP Summary': return <DescriptionOutlinedIcon sx={sx} />;
    case 'Managed Care': return <DescriptionOutlinedIcon sx={sx} />;
    case 'Product Analysis': return <DescriptionOutlinedIcon sx={sx} />;
    case 'Territory Scorecard': return <DescriptionOutlinedIcon sx={sx} />;
    case 'Call Plan Execution': return <DescriptionOutlinedIcon sx={sx} />;
    case 'Territory Summary': return <DescriptionOutlinedIcon sx={sx} />;
    default: return <DescriptionOutlinedIcon sx={sx} />;
  }
};

const ValidationHistory = ({ reports, initialRunId, onClearRunId ,data,onOpenGeoModal,onOpenHcpModal}) => {
  const [selectedDateFilter, setSelectedDateFilter] = useState('Today');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [datePickerAnchorEl, setDatePickerAnchorEl] = useState(null);
  const [customRange, setCustomRange] = useState({ startDate: '', endDate: '' });

  const [historyResults, setHistoryResults] = useState([]);
  const [historySummary, setHistorySummary] = useState({ total: 0, passed: 0, failed: 0, pass_rate: 0 });
  const [fetchedAvailablePages, setFetchedAvailablePages] = useState(null);
  const [isLoadingPages, setIsLoadingPages] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [selectedReportPage, setSelectedReportPage] = useState('');
  const [reportPageSearch, setReportPageSearch] = useState('');
  const [showRunSearch, setShowRunSearch] = useState(false);
  const [runSearchValue, setRunSearchValue] = useState("");

  useEffect(() => {


    console.log('reports initialRunId',data, reports, initialRunId);
    
    const fetchPages = async () => {
      let params = {};
      if (initialRunId) {
        params = { run_id: initialRunId };
      } else {
        if (selectedDateFilter === 'Custom Date' && (!customRange.startDate || !customRange.endDate)) return;
        const dateParams = getDateRange(selectedDateFilter, customRange);
        if (!dateParams.start_date) return;
        params = dateParams;
      }
      
      // 🔥 Reset the selected page and clear the list to cleanly teardown the UI during loading
      setSelectedReportPage('');
      setFetchedAvailablePages(null);
      setIsLoadingPages(true);
      try {
        const pagesResponse = await axios.get('/validation/available-report-pages/', { params });
        if (pagesResponse && pagesResponse.data) {
          const pagesData = Array.isArray(pagesResponse.data) 
            ? pagesResponse.data 
            : (pagesResponse.data.pages || pagesResponse.data.data || []);
          
          setFetchedAvailablePages(pagesData);
        } else {
          setFetchedAvailablePages([]);
        }
      } catch (err) {
        console.error('Failed to fetch pages:', err);
        setFetchedAvailablePages([]);
      } finally {
        setIsLoadingPages(false);
      }
    };
    fetchPages();
  }, [selectedDateFilter, customRange.startDate, customRange.endDate, initialRunId]);

  /* ── 2. Auto-select first page if none selected ── */
  useEffect(() => {
    if (fetchedAvailablePages === null) return; // wait until fetched
    const opts = fetchedAvailablePages.length > 0 ? fetchedAvailablePages : (reports && reports.length ? reports : []);
    
    if (opts.length > 0) {
      if (!selectedReportPage || !opts.includes(selectedReportPage)) {
        setSelectedReportPage(opts[0]);
      }
    } else {
      setSelectedReportPage('');
    }
  }, [fetchedAvailablePages, reports, selectedReportPage]);

  /* ── 3. Fetch Results & Summary by selected page ── */
  useEffect(() => {
    if (isLoadingPages) return; // Prevent ghost requests while pages are resolving
    
    if (!selectedReportPage) {
      setHistoryResults([]);
      setHistorySummary({ total: 0, passed: 0, failed: 0, pass_rate: 0 });
      return;
    }
    
    // Prevent fetching if the selected page is stale or if there are no available pages
    if (fetchedAvailablePages !== null && !fetchedAvailablePages.includes(selectedReportPage)) {
      return;
    }

    const fetchResults = async () => {
      setIsLoadingHistory(true);
      let params = { report_page_name: selectedReportPage };
      
      if (initialRunId) {
        params.run_id = initialRunId;
      } else {
        if (selectedDateFilter === 'Custom Date' && (!customRange.startDate || !customRange.endDate)) {
          setIsLoadingHistory(false);
          return;
        }
        const dateParams = getDateRange(selectedDateFilter, customRange);
        if (!dateParams.start_date) {
          setIsLoadingHistory(false);
          return;
        }
        params = { ...params, ...dateParams };
      }

      try {
        const response = await axios.get('/validation-results/', { params });
        setHistoryResults(response.data?.data || []);
        if (response.data?.summary) setHistorySummary(response.data.summary);
      } catch (err) {
        console.error('Failed to fetch history results:', err);
      } finally {
        setIsLoadingHistory(false);
      }
    };
    fetchResults();
  }, [selectedDateFilter, customRange.startDate, customRange.endDate, initialRunId, selectedReportPage, isLoadingPages, fetchedAvailablePages]);

  const handleDateFilterChange = (filterLabel, anchorEl = null) => {
    if (filterLabel !== 'Custom Date') {
      setIsLoadingPages(true);
      setSelectedReportPage('');
      setFetchedAvailablePages(null);
    }
    setSelectedDateFilter(filterLabel);
    if (filterLabel === 'Custom Date') {
      setDatePickerAnchorEl(anchorEl);
      setShowDatePicker(true);
      return;
    }
    setShowDatePicker(false);
    setDatePickerAnchorEl(null);
    if (selectedDateFilter === 'Custom Date') {
      setCustomRange({ startDate: '', endDate: '' });
    }
  };

  const handleCustomRangeFetch = (range) => {
    setIsLoadingPages(true);
    setSelectedReportPage('');
    setFetchedAvailablePages(null);
    setCustomRange({ startDate: range.startDate, endDate: range.endDate });
  };

  const handleDatePickerClose = () => {
    setShowDatePicker(false);
    setDatePickerAnchorEl(null);
  };

  const historyColumns = useMemo(() => [
    // {
    //   accessorKey: 'report_page_name',
    //   header: 'Report Page',
    //   minSize: 130,
    //   size: 150,
    //   Cell: ({ row }) => (
    //     <Typography variant="body2" sx={{ color: '#2563EB', fontWeight: 700, fontSize: '0.8rem' }}>
    //       {row.original.report_page_name}
    //     </Typography>
    //   )
    // },
    {
      accessorKey: 'geo_level',
      header: 'Geo Level',
      minSize: 90,
      size: 110,
      Cell: ({ row }) => (
        <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, fontSize: '0.78rem' }}>
          {row.original.geo_level}
        </Typography>
      )
    },
    {
      accessorKey: 'geo_filter',
      header: 'Geo Filter',
      minSize: 90,
      size: 110,
      Cell: ({ row }) => (
        <Typography sx={{ fontSize: '0.8rem', color: '#475569' }}>
          {row.original.geo_filter}
        </Typography>
      )
    },
    {
      accessorKey: 'metric_name',
      header: 'Metric',
      minSize: 150,
      size: 170,
      Cell: ({ row }) => (
        <Typography variant="body2" sx={{ fontWeight: 700, color: '#1E293B', fontSize: '0.8rem' }}>
          {row.original.metric_name}
        </Typography>
      )
    },
    {
      accessorKey: 'snowflake_value',
      header: 'IC',
      minSize: 100,
      size: 120,
      Cell: ({ row }) => {
        const val = row.original.snowflake_value;
        if (val === null || val === undefined) return <span style={{ color: '#CBD5E1' }}>—</span>;
        return <span style={{ fontWeight: 500, color: '#334155', fontSize: '0.8rem' }}>{val}</span>;
      }
    },
    {
      accessorKey: 'powerbi_value',
      header: 'Apex',
      minSize: 90,
      size: 110,
      Cell: ({ row }) => {
        const val = row.original.powerbi_value;
        if (val === null || val === undefined) return <span style={{ color: '#CBD5E1' }}>—</span>;
        return <span style={{ fontWeight: 500, color: '#334155', fontSize: '0.8rem' }}>{val}</span>;
      }
    },
    {
      accessorKey: 'difference',
      header: 'Diff',
      minSize: 80,
      size: 80,
      Cell: ({ row }) => {
        const val = row.original.difference;
        if (val === null || val === undefined) return <span style={{ color: '#CBD5E1' }}>—</span>;
        const isFail = row.original.status === 'FAIL';
        return (
          <Box sx={{
            display: 'inline-flex', px: 1.2, py: 0.3, borderRadius: '8px',
            bgcolor: isFail ? '#FEF2F2' : (val === 0 ? '#F0FDF4' : '#FFF7ED'),
            color: isFail ? '#DC2626' : (val === 0 ? '#16A34A' : '#D97706'),
            fontWeight: 700, fontSize: '0.78rem'
          }}>
            {isFail && val > 0 ? '+' : ''}{val}
          </Box>
        );
      }
    },
    {
      accessorKey: 'status',
      header: 'Status',
      minSize: 110,
      size: 130,
      Cell: ({ row }) => {
        const isPass = row.original.status === 'PASS';
        return (
          <Box sx={{
            display: 'inline-flex', alignItems: 'center', px: 1.5, py: 0.4, borderRadius: '20px',
            bgcolor: isPass ? '#F0FDF4' : '#FEF2F2',
            border: `1px solid ${isPass ? '#BBF7D0' : '#FECACA'}`,
            fontSize: '0.75rem', fontWeight: 700,
            color: isPass ? '#16A34A' : '#DC2626'
          }}>
           {row.original.status}
          </Box>
        );
      }
    },
    {
      accessorKey: 'validated_date',
      header: 'Validated Date',
      minSize: 130,
      size: 150,
      Cell: ({ row }) => (
        <Box>
          <Typography variant="caption" sx={{ color: '#475569', fontWeight: 600, fontSize: '0.75rem', display: 'block' }}>
            {row.original.validated_date ? new Date(row.original.validated_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
          </Typography>
          <Typography variant="caption" sx={{ color: '#94A3B8', fontSize: '0.68rem' }}>
            {row.original.validated_date ? new Date(row.original.validated_date).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : ''}
          </Typography>
        </Box>
      )
    },
    {
      accessorKey: 'validated_by',
      header: 'By',
      minSize: 80,
      size: 80,
      Cell: ({ row }) => (
        <Typography sx={{ fontSize: '0.8rem', color: '#64748B', fontWeight: 600 }}>
          {row.original.validated_by}
        </Typography>
      )
    }
  ], []);

  const statCards = [
    { label: 'Total Validated', value: historySummary.total.toLocaleString(), sub: `${selectedDateFilter} period`, subColor: '#16A34A', icon: <CheckCircleOutlineIcon sx={{ fontSize: 20 }} />, iconBg: '#EFF6FF', iconColor: '#2563EB', showTrend: true },
    { label: 'Passed', value: historySummary.passed.toLocaleString(), sub: '✓ Validation passed', subColor: '#16A34A', icon: <CheckCircleOutlineIcon sx={{ fontSize: 20 }} />, iconBg: '#F0FDF4', iconColor: '#16A34A' },
    { label: 'Failed', value: historySummary.failed, sub: historySummary.failed > 0 ? '⚠ Requires review' : '✓ No issues found', subColor: historySummary.failed > 0 ? '#DC2626' : '#16A34A', icon: <WarningAmberRoundedIcon sx={{ fontSize: 20 }} />, iconBg: historySummary.failed > 0 ? '#FEF2F2' : '#F8FAFC', iconColor: historySummary.failed > 0 ? '#DC2626' : '#94A3B8', valueColor: historySummary.failed > 0 ? '#DC2626' : '#0F172A' },
    { label: 'Accuracy Rate', value: `${historySummary.pass_rate}%`, icon: <SpeedRoundedIcon sx={{ fontSize: 20 }} />, iconBg: '#EEF2FF', iconColor: '#4F46E5', progressBar: true },
  ];

  const reportPageOptions = fetchedAvailablePages || (reports && reports.length ? reports : []);

  const filteredHistoryResults = historyResults;

  return (
    <Box>
      {/* ─── Detailed Run Information Banner ─── */}
      {initialRunId && (
        <Box sx={{ px: 2.5, py: 2,
          bgcolor: '#FFFFFF', 
          border: '1px solid #E2E8F0', 
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(15,23,42,0.04)',
          display: 'flex', 
          flexDirection: 'column', 
          gap: 1.5
        }}>
          {/* Top row: Title and Status */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
               <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: '#0F172A' }}>
                 Validation Run Details
               </Typography>
               <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#64748B', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                 • <span style={{ color: '#475569' }}>
                    {data?.run_time ? new Date(data.run_time).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 
                    (historyResults?.length > 0 ? new Date(historyResults[0].validated_date).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '...')}
                   </span>
               </Typography>
            </Box>
            
            {data?.status && (
              <Box sx={{
                display: "inline-flex", alignItems: "center", px: 1.5, py: 0.4, borderRadius: "20px",
                bgcolor: data.status === 'COMPLETED' ? '#F0FDF4' : (data.status === 'COMPLETED_WITH_ERRORS' ? '#FEF9C3' : (data.status === 'FAILED' ? '#FEF2F2' : '#EFF6FF')),
                color: data.status === 'COMPLETED' ? '#166534' : (data.status === 'COMPLETED_WITH_ERRORS' ? '#78350F' : (data.status === 'FAILED' ? '#991B1B' : '#1E40AF')),
                border: `1.5px solid ${data.status === 'COMPLETED' ? '#DCFCE7' : (data.status === 'COMPLETED_WITH_ERRORS' ? '#FEF08A' : (data.status === 'FAILED' ? '#FEE2E2' : '#DBEAFE'))}`,
                fontSize: "0.7rem", fontWeight: 700, textTransform: 'capitalize'
              }}>
                {data.status.replace(/_/g, ' ').toLowerCase()}
              </Box>
            )}
          </Box>
          
          <Box sx={{ height: '1px', bgcolor: '#F1F5F9', width: '100%' }} />

          {/* Bottom row: Other metrics */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 4, md: 6 } }}>
            {/* Run By */}
            <Box>
              <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.2 }}>Run By</Typography>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#334155' }}>
                {data?.validated_by || 'system'}
              </Typography>
            </Box>

            {/* Rollup */}
            <Box>
              <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.2 }}>Rollup</Typography>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#334155' }}>
                {data?.rollup || '-'}
              </Typography>
            </Box>
            
            {/* Geo Levels */}
            <Box>
              <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.2 }}>Geo Levels</Typography>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#334155' }}>
                {data?.geo_levels && data.geo_levels.length > 0 ? data.geo_levels.join(', ') : 'None'}
              </Typography>
            </Box>

                       <Box>
              <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.2 }}>Geo Names</Typography>
              {(data?.geo_names?.regions?.length > 0 || data?.geo_names?.territories?.length > 0) ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography                     onClick={() => onOpenGeoModal && onOpenGeoModal(data.geo_names)}
 sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#334155',underline: 'hover', cursor: 'pointer' }}>
                    {[
                      data.geo_names.regions?.length ? `${data.geo_names.regions.length} region${data.geo_names.regions.length > 1 ? 's' : ''}` : null,
                      data.geo_names.territories?.length ? `${data.geo_names.territories.length} territor${data.geo_names.territories.length > 1 ? 'ies' : 'y'}` : null
                    ].filter(Boolean).join(' • ') || '-'}
                  </Typography>
                  {/* <IconButton
                    size="small"
                    onClick={() => onOpenGeoModal && onOpenGeoModal(data.geo_names)}
                    sx={{ color: "#2563EB", p: 0.2 }}
                  >
                    <VisibilityRoundedIcon sx={{ fontSize: 16 }} />
                  </IconButton> */}
                </Box>
              ) : (
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#334155' }}>
                  -
                </Typography>
              )}
            </Box>
 
            {/* HCP Inputs */}
            <Box>
              <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.2 }}>HCP Inputs</Typography>
              {data?.hcp_inputs?.length > 0 ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5,marginLeft: '20px' }}>
                 
                  <IconButton
                    size="small"
                    onClick={() => onOpenHcpModal && onOpenHcpModal(data.hcp_inputs)}
                    sx={{ color: "#2563EB", p: 0.2 }}
                  >
                    <VisibilityRoundedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              ) : (
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#334155' }}>
                  -
                </Typography>
              )}
            </Box>

          </Box>
        </Box>
      )}

      {/* ─── Header + Date Filter ─── */}
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'space-between', width: '100%', mb: 2.5, gap: 3 ,mt:1.5}}>
        
        {/* Left Side: Available Pages */}
        <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
          <Typography sx={{ color: '#94A3B8', fontSize: '0.7rem', fontWeight: 600, mb: 0.5 }}>
            Available Pages ({reportPageOptions.length})
          </Typography>
          <Box sx={{
            display: 'flex', gap: 1, overflowX: 'auto', pb: 0.5,
            '&::-webkit-scrollbar': { height: '4px' },
            '&::-webkit-scrollbar-thumb': { background: '#CBD5E1', borderRadius: '4px' },
            '&::-webkit-scrollbar-track': { background: 'transparent' }
          }}>
            {isLoadingPages ? (
              Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} variant="rounded" width={110} height={32} sx={{ borderRadius: '8px', bgcolor: '#F1F5F9' }} />
              ))
            ) : (
              reportPageOptions.map((page) => {
                const isSelected = selectedReportPage === page;
                return (
                  <Button
                    key={page}
                    variant="text"
                    onClick={() => setSelectedReportPage(page)}
                    sx={{
                      display: 'flex', alignItems: 'center',
                      minWidth: 'auto',
                      px: 2, py: 0.6,
                      borderRadius: '20px',
                      color: isSelected ? '#FFFFFF' : '#475569',
                      background: isSelected ? '#3B82F6' : '#F1F5F9',
                      border: 'none',
                      fontSize: '0.75rem', fontWeight: 600,
                      textTransform: 'none',
                      whiteSpace: 'nowrap',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': { background: isSelected ? '#2563EB' : '#E2E8F0' }
                    }}
                  >
                    {getPageIcon(page, isSelected)}
                    {page}
                  </Button>
                );
              })
            )}
          </Box>
        </Box>

        {/* Right Side: Date Filter */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1, flexShrink: 0 }}>
          {/* ── Date Filter Tabs (hidden when filtering by runId) ── */}
          {!initialRunId && (
            <Box sx={{
              display: 'inline-flex', alignItems: 'center',
              background: '#E2E8F0', borderRadius: '12px',
              border: '1px solid #3B82F6',
              p: '3px',
              boxShadow: 'inset 0 1px 3px rgba(15,23,42,0.06)'
            }}>
              {DATE_FILTERS.map((filterLabel) => {
                const isSelected = selectedDateFilter === filterLabel;
                return (
                  <Button
                    key={filterLabel}
                    variant="text"
                    disableRipple
                    onClick={(event) => handleDateFilterChange(
                      filterLabel,
                      filterLabel === 'Custom Date' ? event.currentTarget : null
                    )}
                    sx={{
                      minWidth: filterLabel === 'Today' ? '90px' : '130px',
                      height: '34px',
                      px: 2,
                      borderRadius: '8px',
                      color: isSelected ? '#0F172A' : '#475569',
                      background: isSelected ? '#FFFFFF' : 'transparent',
                      boxShadow: isSelected ? '0 2px 4px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06)' : 'none',
                      fontSize: '12.5px', fontWeight: isSelected ? 700 : 500, textTransform: 'none',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': { 
                        color: isSelected ? '#0F172A' : '#1E293B',
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                      {filterLabel === 'Today' ? <TodayRoundedIcon sx={{ fontSize: 16, color: isSelected ? '#2563EB' : '#94A3B8' }} /> : <DateRangeRoundedIcon sx={{ fontSize: 16, color: isSelected ? '#2563EB' : '#94A3B8' }} />}
                      {filterLabel}
                    </Box>
                  </Button>
                );
              })}
            </Box>
          )}

          {/* ── CustomDateRange Popover ── */}
          {!initialRunId && (
            <Popover
              open={selectedDateFilter === 'Custom Date' && showDatePicker}
              anchorEl={datePickerAnchorEl}
              onClose={handleDatePickerClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{ sx: { mt: 1, borderRadius: '8px', boxShadow: '2px 2px 8px 0px rgba(0,0,0,0.30)', overflow: 'visible' } }}
            >
              <SingleDatePicker
                date={customRange.startDate}
                showDatePicker={showDatePicker}
                setShowDatePicker={setShowDatePicker}
                usePopover
                onFetch={handleCustomRangeFetch}
              />
            </Popover>
          )}
        </Box>
      </Box>

      {(!isLoadingPages && fetchedAvailablePages !== null && fetchedAvailablePages.length === 0) ? (
        <Box sx={{ 
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          py: 10, px: 2, bgcolor: '#F8FAFC', borderRadius: '16px', border: '1px dashed #CBD5E1', mt: 2
        }}>
          <Box sx={{ 
            width: 80, height: 80, borderRadius: '50%', bgcolor: '#F1F5F9', 
            display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 
          }}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="9" y1="15" x2="15" y2="15"></line>
            </svg>
          </Box>
          <Typography variant="h6" sx={{ color: '#334155', fontWeight: 600, fontSize: '1.05rem', mb: 0.5 }}>
            No validation runs found
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748B', maxWidth: 350, textAlign: 'center' }}>
            No records found for{' '}
            <strong style={{ color: '#334155' }}>
              {selectedDateFilter === 'Custom Date' && customRange.startDate
                ? new Date(customRange.startDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                : `Today — ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`
              }
            </strong>
            . Try selecting a different date range.
          </Typography>
        </Box>
      ) : (
        <>
          {/* ─── Page Tabs & Summary Cards ─── */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: "1.5rem",
            width: '100%'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2, flexWrap: 'wrap' }}>
            {(isLoadingHistory || isLoadingPages) ? (
              <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} variant="rounded" sx={{ flex: 1, height: 48, borderRadius: '12px', bgcolor: '#F1F5F9' }} />
                ))}
              </Box>
            ) : (
              statCards.map((card) => (
                <Box
                  key={card.label}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flex: '1 1 0',
                    minWidth: '190px',
                    gap: 1.5,
                    px: 2,
                    py: 1.5,
                    borderRadius: '12px',
                    bgcolor: '#FFFFFF',
                    border: '1px solid #F1F5F9',
                    boxShadow: '0 1px 4px rgba(15,23,42,0.06)',
                    whiteSpace: 'nowrap',
                    transition: 'transform 0.18s ease',
                    '&:hover': { transform: 'translateY(-1px)' }
                  }}
                >
                  <Box
                    sx={{
                      p: 0.8,
                      borderRadius: '8px',
                      bgcolor: card.iconBg,
                      color: card.iconColor,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {React.cloneElement(card.icon, { sx: { fontSize: 20 } })}
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', pr: 0.5 }}>
                    <Typography sx={{ color: '#94A3B8', fontWeight: 600, fontSize: '0.68rem', lineHeight: 1.2, textTransform: 'uppercase', letterSpacing: '0.03em', mb: 0.3 }}>
                      {card.label}
                    </Typography>
                    <Typography sx={{ color: card.valueColor || '#0F172A', fontWeight: 700, fontSize: '1.15rem', lineHeight: 1 }}>
                      {card.value}
                    </Typography>
                  </Box>
                </Box>
              ))
            )}
          </Box>
        </Box>

      {/* ─── Table ─── */}
      <Card sx={{ borderRadius: '16px', border: '1px solid #EEF2F7', overflow: 'hidden', boxShadow: 'none', bgcolor: '#FFF' }}>
        {(isLoadingHistory || isLoadingPages) ? (
          <Box>
            <Box sx={{ bgcolor: '#F8FAFC', borderBottom: '2px solid #F1F5F9', px: 2, py: 1.5, display: 'flex', gap: 2 }}>
              {['Report Page', 'Level', 'Filter', 'Metric', 'Snowflake', 'PowerBI', 'Diff', 'Status', 'Date', 'By'].map((h) => (
                <Skeleton key={h} variant="text" width={h === 'Metric' || h === 'Report Page' ? 100 : 60} height={14} sx={{ bgcolor: '#E2E8F0' }} />
              ))}
            </Box>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody><TableSkeletonRows rows={8} cols={10} /></tbody>
            </table>
            <Box sx={{ borderTop: '1px solid #F1F5F9', p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Skeleton variant="text" width={200} height={16} sx={{ bgcolor: '#F1F5F9' }} />
              <Box sx={{ display: 'flex', gap: 1 }}>
                {[1, 2, 3].map(n => <Skeleton key={n} variant="rounded" width={32} height={32} sx={{ borderRadius: '8px', bgcolor: '#F1F5F9' }} />)}
              </Box>
            </Box>
          </Box>
        ) : (
          <GlobalDataTable
            data={filteredHistoryResults}
            columns={historyColumns}
            paginator
            enableTopToolbar
            enableToolbarInternalActions
            enableGlobalFilter={false}
            enableColumnFilters={true}
            initialState={{
              showColumnFilters: false,
            }}
            rows={10}
            style={{ borderRadius: "16px" }}
            className="p-datatable-striped custom-data-table px-3"
            muiTablePaperProps={{
              sx: {
                border: "none",
                borderRadius: "16px",
                bgcolor: "#FFFFFF",
              },
            }}
            muiTopToolbarProps={{
              sx: {
                alignItems: "center",
                borderBottom: "1px solid #E2E8F0",
                boxShadow: "none",
                px: 0.7,
                py: 0.7,
                "& .MuiBox-root": {
                  alignItems: "center",
                },
              },
            }}
            renderTopToolbarCustomActions={() => (
              <Box>
                <Typography sx={{ fontWeight: 600, color: "#0F172A", fontSize: "15px", marginTop: "0px" }}>
                  {initialRunId ? "Run Reports" : "History Results"}
                  {selectedReportPage && (
                    <Box
                      component="span"
                      sx={{
                        ml: 0.8,
                        px: 1.2,
                        py: 0.2,
                        borderRadius: '20px',
                        bgcolor: '#EFF6FF',
                        color: '#2563EB',
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        verticalAlign: 'middle',
                        letterSpacing: '0.01em',
                      }}
                    >
                      {selectedReportPage}
                    </Box>
                  )}
                </Typography>
                <Typography sx={{ color: "#94A3B8", fontWeight: 500, fontSize: "11.5px" }}>
                  {initialRunId
                    ? `Filtered by run on ${
                        historyResults?.length > 0
                          ? new Date(historyResults[0].validated_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                          : '...'
                      }`
                    : selectedDateFilter === 'Custom Date' && customRange.startDate
                      ? `Showing results for ${new Date(customRange.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                      : `Showing results for Today — ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                  }
                </Typography>
              </Box>
            )}
            renderToolbarInternalActions={({ table }) => (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {showRunSearch && (
                  <TextField
                    autoFocus
                    size="small"
                    placeholder="Search"
                    value={runSearchValue}
                    onChange={(event) => {
                      const value = event.target.value;
                      setRunSearchValue(value);
                      table.setGlobalFilter(value || undefined);
                    }}
                    InputProps={{
                      endAdornment: runSearchValue ? (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="Clear search"
                            size="small"
                            onClick={() => {
                              setRunSearchValue("");
                              table.setGlobalFilter(undefined);
                            }}
                            edge="end"
                          >
                            <CloseRoundedIcon />
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                    }}
                    sx={{
                      width: 190,
                      "& .MuiOutlinedInput-root": {
                        bgcolor: "#FFFFFF",
                        borderRadius: "8px",
                        fontSize: "12.5px",
                        height: 36,
                        "& fieldset": { borderColor: "#DDE3EA" },
                        "&:hover fieldset": { borderColor: "#CBD5E1" },
                        "&.Mui-focused fieldset": {
                          borderColor: "#93C5FD",
                          borderWidth: "1px",
                        },
                      },
                      "& .MuiInputBase-input": {
                        py: 0,
                        "&::placeholder": { color: "#94A3B8", opacity: 1 },
                      },
                      "& .MuiInputAdornment-root": { mr: -0.5 },
                      "& .MuiIconButton-root": {
                        color: "#94A3B8",
                        height: 26,
                        p: 0.25,
                        width: 26,
                      },
                      "& .MuiIconButton-root:hover": {
                        bgcolor: "transparent",
                        color: "#64748B",
                      },
                      "& .MuiSvgIcon-root": { fontSize: 18 },
                    }}
                  />
                )}
                <IconButton
                  size="small"
                  aria-label={showRunSearch ? "Close search" : "Search results"}
                  onClick={() => {
                    if (showRunSearch) {
                      setRunSearchValue("");
                      table.setGlobalFilter(undefined);
                      setShowRunSearch(false);
                    } else {
                      setShowRunSearch(true);
                    }
                  }}
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: "8px",
                    bgcolor: "transparent",
                    color: "#334155",
                    boxShadow: "none",
                    p: "6px",
                    "&:hover": { bgcolor: "transparent", boxShadow: "none" },
                    "& .MuiSvgIcon-root": { fontSize: 18 },
                  }}
                >
                  {showRunSearch ? <SearchOffRoundedIcon /> : <SearchIcon />}
                </IconButton>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.2, borderRadius: "0", p: 0 }}>
                  <MrtToggleFiltersButton
                    table={table}
                    sx={{ width: 30, height: 30, p: "6px", borderRadius: "8px", color: "#334155", bgcolor: "transparent", "&:hover": { bgcolor: "transparent" }, "& .MuiSvgIcon-root": { fontSize: 18 } }}
                  />
                  <MrtShowHideColumnsButton
                    table={table}
                    sx={{ width: 30, height: 30, p: "6px", borderRadius: "8px", color: "#334155", bgcolor: "transparent", "&:hover": { bgcolor: "transparent" }, "& .MuiSvgIcon-root": { fontSize: 18 } }}
                  />
                  <MrtToggleDensePaddingButton
                    table={table}
                    sx={{ width: 30, height: 30, p: "6px", borderRadius: "8px", color: "#334155", bgcolor: "transparent", "&:hover": { bgcolor: "transparent" }, "& .MuiSvgIcon-root": { fontSize: 18 } }}
                  />
                  <MrtToggleFullScreenButton
                  title={table.getState().isFullScreen ? "Exit Full Screen" : "Full Screen"}
                    table={table}
                    sx={{ width: 30, height: 30, p: "6px", borderRadius: "8px", color: "#334155", bgcolor: "transparent", "&:hover": { bgcolor: "transparent" }, "& .MuiSvgIcon-root": { fontSize: 18 } }}
                  />
                </Box>
              </Box>
            )}
            muiTableContainerProps={{
              sx: {
                borderTop: "none",
                maxHeight: "650px",
              },
            }}
            muiTableProps={{
              sx: {
                // width: "max-content",
                "& thead th:last-of-type, & tbody td:last-of-type": {
                  borderRight: "none",
                  pr: 0.5,
                },
              },
            }}
            muiTableHeadRowProps={{
              sx: {
                boxShadow: "none",
              },
            }}
            muiTableHeadCellProps={{
              sx: {
                bgcolor: "#F8FAFC",
                borderBottom: "1px solid #F1F5F9",
                borderRight: "none",
                color: "#242627 !important",
                fontSize: "12px",
                fontWeight: 700,
                letterSpacing: 0,
                px: 1,
                textAlign: "left",
                textTransform: "none",
                verticalAlign: "middle",
                boxShadow: "none",
                "& .Mui-TableHeadCell-Content": {
                  justifyContent: "flex-start",
                  width: "100%",
                },
                "& .Mui-TableHeadCell-Content-Labels": {
                  justifyContent: "flex-start",
                },
                "&:last-of-type": {
                  pr: 0.5,
                },
              },
            }}
            muiTableBodyCellProps={{
              sx: {
                bgcolor: "#FFFFFF",
                borderBottom: "1px solid #EEF0F3",
                borderRight: "none",
                color: "#1F2937",
                fontSize: "12px",
                fontWeight: 500,
                px: 1,
                textAlign: "left",
                verticalAlign: "middle",
                "& > *": { marginLeft: 0 },
                "&:last-of-type": { pr: 0.5 },
              },
            }}
            muiBottomToolbarProps={{
              sx: {
                borderTop: "1px solid #D7DCE2",
                boxShadow: "none",
                minHeight: "42px",
                "& .MuiTablePagination-root, & .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiSelect-select":
                  {
                    fontFamily: "inherit",
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#334155",
                  },
                "& .MuiIconButton-root": {
                  color: "#64748B",
                  p: 0.5,
                },
              },
            }}
            muiTableBodyRowProps={({ row }) => ({
              sx: {
                bgcolor: row.original.status === 'FAIL' ? '#FFFBFB' : '#FFFFFF',
                "&:hover": {
                  bgcolor: row.original.status === 'FAIL' ? '#FEF2F2' : '#F8FAFC',
                },
                "& .MuiTableCell-root": {
                  borderBottom: "1px solid #F1F5F9",
                },
              },
            })}
          />
        )}
      </Card>
        </>
      )}


    </Box>
  );
};

export default ValidationHistory;
